/**
 * Building type definitions and constants.
 * All building stats are configurable from this central location.
 * Architecture supports future database storage and retrieval.
 */

import { useDevSettingsStore } from '@shared/stores/devSettingsStore';

/**
 * Building type enum.
 */
export enum BuildingType {
  TOWER_ARCHER = 'tower_archer',
  TOWER_CANNON = 'tower_cannon',
  TOWER_MAGIC = 'tower_magic',
  WALL = 'wall',
  BARRACKS = 'barracks',
  TECH_BUILDING = 'tech_building',
  HERO_SPAWNER = 'hero_spawner',
  MINE = 'mine',
}

/**
 * Building dimensions (width x height in tiles).
 */
export interface BuildingDimensions {
  width: number;
  height: number;
}

/**
 * Tower damage type enum.
 */
export enum TowerDamageType {
  PIERCING = 'piercing',
  SPLASH = 'splash',
  MAGIC = 'magic',
}

/**
 * Tower combat stats (only for tower types).
 */
export interface TowerCombatStats {
  /** Base damage per attack */
  damage: number;
  /** Attack range in tiles */
  range: number;
  /** Time between attacks in seconds */
  attackSpeed: number;
  /** Damage type */
  damageType: TowerDamageType;
}

/**
 * Building stats (health and armor for all buildings).
 */
export interface BuildingStats {
  /** Maximum health points */
  health: number;
  /** Armor value (reduces incoming damage) */
  armor: number;
}

/**
 * Building definition with dimensions, display name, stats, costs, and construction times.
 */
export interface BuildingDefinition {
  type: BuildingType;
  name: string;
  dimensions: BuildingDimensions;
  requiresMineNode?: boolean; // If true, can only be placed on mine nodes
  stats: BuildingStats; // Health and armor
  cost: number; // Construction cost in gold
  constructionTime: number; // Construction time in seconds
  towerStats?: TowerCombatStats; // Only for tower types
}

/**
 * Building definitions map.
 * All building stats, costs, and construction times are defined here for easy configuration.
 */
export const BUILDING_DEFINITIONS: Record<BuildingType, BuildingDefinition> = {
  [BuildingType.TOWER_ARCHER]: {
    type: BuildingType.TOWER_ARCHER,
    name: 'Archer Tower',
    dimensions: { width: 2, height: 2 },
    stats: { health: 200, armor: 5 },
    cost: 50,
    constructionTime: 20,
    towerStats: {
      damage: 15,
      range: 7,
      attackSpeed: 0.6,
      damageType: TowerDamageType.PIERCING,
    },
  },
  [BuildingType.TOWER_CANNON]: {
    type: BuildingType.TOWER_CANNON,
    name: 'Cannon Tower',
    dimensions: { width: 2, height: 2 },
    stats: { health: 200, armor: 5 },
    cost: 100,
    constructionTime: 20,
    towerStats: {
      damage: 50,
      range: 5,
      attackSpeed: 1.5,
      damageType: TowerDamageType.SPLASH,
    },
  },
  [BuildingType.TOWER_MAGIC]: {
    type: BuildingType.TOWER_MAGIC,
    name: 'Magic Tower',
    dimensions: { width: 2, height: 2 },
    stats: { health: 200, armor: 5 },
    cost: 75,
    constructionTime: 20,
    towerStats: {
      damage: 20,
      range: 9,
      attackSpeed: 0.75,
      damageType: TowerDamageType.MAGIC,
    },
  },
  [BuildingType.WALL]: {
    type: BuildingType.WALL,
    name: 'Wall',
    dimensions: { width: 2, height: 1 },
    stats: { health: 200, armor: 10 },
    cost: 50,
    constructionTime: 5,
  },
  [BuildingType.BARRACKS]: {
    type: BuildingType.BARRACKS,
    name: 'Barracks',
    dimensions: { width: 4, height: 3 },
    stats: { health: 500, armor: 5 },
    cost: 500,
    constructionTime: 60,
  },
  [BuildingType.TECH_BUILDING]: {
    type: BuildingType.TECH_BUILDING,
    name: 'Tech Building',
    dimensions: { width: 2, height: 2 },
    stats: { health: 300, armor: 3 },
    cost: 300,
    constructionTime: 45,
  },
  [BuildingType.HERO_SPAWNER]: {
    type: BuildingType.HERO_SPAWNER,
    name: 'Hero Spawner',
    dimensions: { width: 2, height: 2 },
    stats: { health: 500, armor: 5 },
    cost: 750,
    constructionTime: 120,
  },
  [BuildingType.MINE]: {
    type: BuildingType.MINE,
    name: 'Mine',
    dimensions: { width: 2, height: 2 },
    requiresMineNode: true,
    stats: { health: 200, armor: 3 },
    cost: 50,
    constructionTime: 20,
  },
};

/**
 * Get building definition by type.
 * Merges base definition with dev settings if available.
 */
export function getBuildingDefinition(type: BuildingType): BuildingDefinition {
  const base = BUILDING_DEFINITIONS[type];

  // Try to get dev settings (may not be available in all contexts)
  try {
    const devSettings = useDevSettingsStore.getState().settings.buildings;

    // Create a merged definition with dev settings
    const merged: BuildingDefinition = { ...base };

    // Override construction time based on building type (only if value exists and is valid)
    if (
      type === BuildingType.TOWER_ARCHER ||
      type === BuildingType.TOWER_CANNON ||
      type === BuildingType.TOWER_MAGIC
    ) {
      if (devSettings.towerConstructionTime != null && devSettings.towerConstructionTime > 0) {
        merged.constructionTime = devSettings.towerConstructionTime;
      }
    } else if (type === BuildingType.MINE) {
      if (devSettings.mineConstructionTime != null && devSettings.mineConstructionTime > 0) {
        merged.constructionTime = devSettings.mineConstructionTime;
      }
    } else if (type === BuildingType.BARRACKS) {
      if (
        devSettings.barracksConstructionTime != null &&
        devSettings.barracksConstructionTime > 0
      ) {
        merged.constructionTime = devSettings.barracksConstructionTime;
      }
    } else if (type === BuildingType.HERO_SPAWNER) {
      if (
        devSettings.heroSpawnerConstructionTime != null &&
        devSettings.heroSpawnerConstructionTime > 0
      ) {
        merged.constructionTime = devSettings.heroSpawnerConstructionTime;
      }
    } else if (type === BuildingType.TECH_BUILDING) {
      if (
        devSettings.techBuildingConstructionTime != null &&
        devSettings.techBuildingConstructionTime > 0
      ) {
        merged.constructionTime = devSettings.techBuildingConstructionTime;
      }
    }

    // Override cost based on building type (only if value exists and is valid)
    if (type === BuildingType.TOWER_ARCHER) {
      if (devSettings.towerArcherCost != null && devSettings.towerArcherCost > 0) {
        merged.cost = devSettings.towerArcherCost;
      }
    } else if (type === BuildingType.TOWER_CANNON) {
      if (devSettings.towerCannonCost != null && devSettings.towerCannonCost > 0) {
        merged.cost = devSettings.towerCannonCost;
      }
    } else if (type === BuildingType.TOWER_MAGIC) {
      if (devSettings.towerMagicCost != null && devSettings.towerMagicCost > 0) {
        merged.cost = devSettings.towerMagicCost;
      }
    } else if (type === BuildingType.BARRACKS) {
      if (devSettings.barracksCost != null && devSettings.barracksCost > 0) {
        merged.cost = devSettings.barracksCost;
      }
    } else if (type === BuildingType.HERO_SPAWNER) {
      if (devSettings.heroSpawnerCost != null && devSettings.heroSpawnerCost > 0) {
        merged.cost = devSettings.heroSpawnerCost;
      }
    } else if (type === BuildingType.TECH_BUILDING) {
      if (devSettings.techBuildingCost != null && devSettings.techBuildingCost > 0) {
        merged.cost = devSettings.techBuildingCost;
      }
    } else if (type === BuildingType.MINE) {
      if (devSettings.mineCost != null && devSettings.mineCost > 0) {
        merged.cost = devSettings.mineCost;
      }
    }

    // Override health and armor (only if values exist and are valid)
    if (
      type === BuildingType.TOWER_ARCHER ||
      type === BuildingType.TOWER_CANNON ||
      type === BuildingType.TOWER_MAGIC
    ) {
      if (devSettings.towerHealth != null && devSettings.towerHealth > 0) {
        merged.stats.health = devSettings.towerHealth;
      }
      if (devSettings.towerArmor != null && devSettings.towerArmor >= 0) {
        merged.stats.armor = devSettings.towerArmor;
      }
    } else if (type === BuildingType.MINE) {
      if (devSettings.mineHealth != null && devSettings.mineHealth > 0) {
        merged.stats.health = devSettings.mineHealth;
      }
      if (devSettings.mineArmor != null && devSettings.mineArmor >= 0) {
        merged.stats.armor = devSettings.mineArmor;
      }
    } else if (type === BuildingType.BARRACKS) {
      if (devSettings.barracksHealth != null && devSettings.barracksHealth > 0) {
        merged.stats.health = devSettings.barracksHealth;
      }
      if (devSettings.barracksArmor != null && devSettings.barracksArmor >= 0) {
        merged.stats.armor = devSettings.barracksArmor;
      }
    } else if (type === BuildingType.HERO_SPAWNER) {
      if (devSettings.heroSpawnerHealth != null && devSettings.heroSpawnerHealth > 0) {
        merged.stats.health = devSettings.heroSpawnerHealth;
      }
      if (devSettings.heroSpawnerArmor != null && devSettings.heroSpawnerArmor >= 0) {
        merged.stats.armor = devSettings.heroSpawnerArmor;
      }
    } else if (type === BuildingType.TECH_BUILDING) {
      if (devSettings.techBuildingHealth != null && devSettings.techBuildingHealth > 0) {
        merged.stats.health = devSettings.techBuildingHealth;
      }
      if (devSettings.techBuildingArmor != null && devSettings.techBuildingArmor >= 0) {
        merged.stats.armor = devSettings.techBuildingArmor;
      }
    } else if (type === BuildingType.WALL) {
      if (devSettings.wallHealth != null && devSettings.wallHealth > 0) {
        merged.stats.health = devSettings.wallHealth;
      }
      if (devSettings.wallArmor != null && devSettings.wallArmor >= 0) {
        merged.stats.armor = devSettings.wallArmor;
      }
    }

    // Final safety check: ensure all critical values are valid
    if (
      !merged.constructionTime ||
      merged.constructionTime <= 0 ||
      isNaN(merged.constructionTime)
    ) {
      merged.constructionTime = base.constructionTime;
    }
    if (!merged.cost || merged.cost <= 0 || isNaN(merged.cost)) {
      merged.cost = base.cost;
    }
    if (!merged.stats.health || merged.stats.health <= 0 || isNaN(merged.stats.health)) {
      merged.stats.health = base.stats.health;
    }
    if (merged.stats.armor == null || merged.stats.armor < 0 || isNaN(merged.stats.armor)) {
      merged.stats.armor = base.stats.armor;
    }

    return merged;
  } catch {
    // Dev settings not available, return base definition
    return base;
  }
}

/**
 * Get building cost by type.
 * Uses getBuildingDefinition to include dev settings overrides.
 */
export function getBuildingCost(buildingType: BuildingType): number {
  return getBuildingDefinition(buildingType).cost;
}

/**
 * Get construction time by type (in seconds).
 * Uses getBuildingDefinition to include dev settings overrides.
 */
export function getConstructionTime(buildingType: BuildingType): number {
  return getBuildingDefinition(buildingType).constructionTime;
}

/**
 * Get building health by type.
 * Uses getBuildingDefinition to include dev settings overrides.
 */
export function getBuildingHealth(buildingType: BuildingType): number {
  return getBuildingDefinition(buildingType).stats.health;
}

/**
 * Get building armor by type.
 * Uses getBuildingDefinition to include dev settings overrides.
 */
export function getBuildingArmor(buildingType: BuildingType): number {
  return getBuildingDefinition(buildingType).stats.armor;
}

/**
 * Check if a building type is a tower type.
 */
export function isTowerType(buildingType: BuildingType): boolean {
  return (
    buildingType === BuildingType.TOWER_ARCHER ||
    buildingType === BuildingType.TOWER_CANNON ||
    buildingType === BuildingType.TOWER_MAGIC
  );
}
